import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { MODULE_PRICES, BASE_PLATFORM_PRICE, rm, PRODUCTS } from "@/data/content";

const PlanBuilder = () => {
  const { t, pick } = useLanguage();
  const [selected, setSelected] = useState(["hr", "ai-chat"]);

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const monthly = useMemo(
    () => BASE_PLATFORM_PRICE + MODULE_PRICES.filter((m) => selected.includes(m.id)).reduce((a, m) => a + m.price, 0),
    [selected]
  );
  const annual = monthly * 10;
  const saving = monthly * 2;

  return (
    <div data-testid="plan-builder" className="glass rounded-3xl p-6 sm:p-10 grid lg:grid-cols-5 gap-10">
      <div className="lg:col-span-3">
        <div className="flex items-center justify-between card-surface rounded-2xl px-5 py-4 mb-4 border-indigo-500/40">
          <div className="flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-indigo-400" />
            <span className="font-semibold text-white">{t("pricing.basePlatform")}</span>
          </div>
          <span className="font-mono2 text-indigo-300">{rm(BASE_PLATFORM_PRICE)}{t("common.perMonth")}</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          {MODULE_PRICES.map((m) => {
            const on = selected.includes(m.id);
            return (
              <button
                key={m.id}
                data-testid={`module-toggle-${m.id}`}
                onClick={() => toggle(m.id)}
                className={`flex items-center justify-between rounded-2xl border px-5 py-4 text-left transition-all duration-300 ${
                  on ? "border-indigo-500/60 bg-indigo-500/10" : "border-slate-800 bg-slate-900/50 hover:border-slate-600"
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${on ? "bg-indigo-500 border-indigo-500" : "border-slate-600"}`}>
                    {on && <Check className="w-3.5 h-3.5 text-white" />}
                  </span>
                  <span className={`text-sm font-medium ${on ? "text-white" : "text-slate-300"}`}>{pick(m.label)}</span>
                </span>
                <span className="font-mono2 text-xs text-slate-400">+{rm(m.price)}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="lg:col-span-2 flex flex-col justify-center">
        <div className="card-surface rounded-2xl p-7 space-y-5">
          <div>
            <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500 mb-1">{t("common.monthly")}</p>
            <p data-testid="builder-monthly" className="text-4xl font-extrabold font-heading text-white">{rm(monthly)}</p>
          </div>
          <div className="h-px bg-slate-800" />
          <div className="flex justify-between items-center">
            <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500">{t("common.annual")}</p>
            <p data-testid="builder-annual" className="text-xl font-bold text-slate-200">{rm(annual)}</p>
          </div>
          <div className="flex justify-between items-center">
            <p className="text-xs font-mono2 uppercase tracking-[0.2em] text-slate-500">{t("common.annualSaving")}</p>
            <p data-testid="builder-saving" className="text-xl font-bold text-emerald-400">{rm(saving)}</p>
          </div>
          <Link
            to="/contact"
            data-testid="builder-subscribe"
            className="mt-2 inline-flex items-center justify-center gap-2 w-full rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3.5 text-sm transition-colors shadow-lg shadow-indigo-500/30"
          >
            {t("common.startSubscription")} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export const bundleMonthly = (modules) =>
  BASE_PLATFORM_PRICE +
  modules.reduce((a, slug) => {
    const m = MODULE_PRICES.find((x) => x.id === slug) || PRODUCTS.find((p) => p.slug === slug);
    const price = MODULE_PRICES.find((x) => x.id === slug)?.price;
    return a + (price || 0);
  }, 0);

export default PlanBuilder;
